import type { TaskSnapshot } from '../stores/types';
import { resolveTaskName } from '../utils/utils';
import { addTaskIdWithAncestors } from './criticalPathFilter';

/** Trim and collapse internal whitespace for search. */
export function normalizeTaskSearchQuery(raw: string): string {
  return String(raw ?? '')
    .trim()
    .replace(/\s+/g, ' ');
}

/** Case-insensitive substring match on the display task name. */
export function taskNameMatchesQuery(
  task: TaskSnapshot | { taskName?: string | null; text?: string | null; name?: string | null },
  query: string,
): boolean {
  const q = normalizeTaskSearchQuery(query);
  if (!q) return false;
  const name = resolveTaskName(task);
  if (!name) return false;
  return name.toLowerCase().includes(q.toLowerCase());
}

export type TaskNameSearchResult = {
  /** Matches + ancestors — use for display filter. */
  visibleIds: Set<string>;
  /** First matching task id in store iteration order (for showTask). */
  firstMatchId: string | null;
  matchCount: number;
};

/**
 * Build the visible id set for task-name search: every name match plus ancestors
 * so WBS context stays in the tree. Empty / whitespace query → empty result
 * (caller should treat as “no search filter”).
 */
export function collectTaskNameSearchVisibleTaskIds(
  allTasks: TaskSnapshot[],
  rawQuery: string,
): TaskNameSearchResult {
  const query = normalizeTaskSearchQuery(rawQuery);
  if (!query) {
    return { visibleIds: new Set(), firstMatchId: null, matchCount: 0 };
  }

  const byId = new Map(allTasks.map((t) => [String(t.id), t]));
  const visibleIds = new Set<string>();
  let firstMatchId: string | null = null;
  let matchCount = 0;

  for (const task of allTasks) {
    if (!taskNameMatchesQuery(task, query)) continue;
    const id = String(task.id);
    matchCount += 1;
    if (firstMatchId == null) firstMatchId = id;
    addTaskIdWithAncestors(visibleIds, id, byId);
  }

  return { visibleIds, firstMatchId, matchCount };
}
